/**
 * Fairdrop Keystore Service
 *
 * Supports two keystore formats:
 * 1. FDS Keystore v1 - Full account payload encrypted (fairdrop/fairdrive)
 * 2. Ethereum Web3 Secret Storage v3 - Private key only (wallet import)
 *
 * Both use scrypt KDF + AES-128-CTR encryption + keccak256 MAC.
 * FDS v1 uses N=262144 (Ethereum standard) since fds-id-ts update.
 *
 * @see https://github.com/fairDataSociety/fds-id-ts/blob/main/docs/fds-keystore-format.md
 */

import { scrypt } from '@noble/hashes/scrypt.js'
import { keccak_256 } from '@noble/hashes/sha3.js'
import { ctr } from '@noble/ciphers/aes.js'
import { bytesToHex, hexToBytes, randomBytes } from '@noble/hashes/utils.js'
import type {
  Account,
  FairdropKeystore,
  FairdropKeystorePayload,
  KeystoreProgress,
} from '../../types'
import { hashPassword } from '../storage'

// FDS v1 keystore scrypt parameters (Ethereum standard)
const FDS_SCRYPT_N = 262144 // 2^18 - Ethereum/FDS standard
const FDS_SCRYPT_R = 8
const FDS_SCRYPT_P = 1
const FDS_SCRYPT_DKLEN = 32

/**
 * Ethereum Web3 Secret Storage v3 keystore format (wallet import)
 */
interface EthereumKeystore {
  version: 3
  id: string
  address: string
  crypto: {
    cipher: 'aes-128-ctr'
    ciphertext: string
    cipherparams: {
      iv: string
    }
    kdf: 'scrypt'
    kdfparams: {
      dklen: number
      n: number
      r: number
      p: number
      salt: string
    }
    mac: string
  }
}

/**
 * Union type for both keystore formats
 */
type AnyKeystore = FairdropKeystore | EthereumKeystore

/**
 * Detect FDS v1 keystore (fairdrop or fairdrive type)
 */
function isFDSKeystore(ks: unknown): ks is FairdropKeystore {
  if (!ks || typeof ks !== 'object') return false
  const obj = ks as Record<string, unknown>
  return obj.version === 1 && (obj.type === 'fairdrop' || obj.type === 'fairdrive')
}

/**
 * Detect Ethereum Web3 v3 keystore (wallet import)
 */
function isEthereumKeystore(ks: unknown): ks is EthereumKeystore {
  if (!ks || typeof ks !== 'object') return false
  const obj = ks as Record<string, unknown>
  return obj.version === 3 && typeof obj.id === 'string'
}

/**
 * Encrypt account data into an FDS keystore (v1)
 */
export async function createFairdropKeystore(
  account: Account,
  password: string,
  onProgress?: (stage: KeystoreProgress) => void
): Promise<string> {
  // Create the payload to encrypt
  const payload: FairdropKeystorePayload = {
    subdomain: account.subdomain,
    publicKey: account.publicKey,
    privateKey: account.privateKey,
    inboxParams: account.inboxParams,
    walletAddress: account.walletAddress,
    created: account.created,
    // Include postage stamp info in backup
    stampId: account.stampId,
    stampExpiresAt: account.stampExpiresAt,
    stampCapacity: account.stampCapacity,
    stampTotalCapacity: account.stampTotalCapacity,
  }

  // Generate random salt and IV
  const salt = randomBytes(32)
  const iv = randomBytes(16)

  // Derive key using scrypt (async for UI responsiveness)
  onProgress?.('deriving-key')
  const derivedKey = await deriveKeyAsync(password, salt, FDS_SCRYPT_N, FDS_SCRYPT_R, FDS_SCRYPT_P, FDS_SCRYPT_DKLEN)

  // Encrypt payload with AES-128-CTR using @noble/ciphers
  onProgress?.('encrypting')
  const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
  const encryptionKey = derivedKey.slice(0, 16)
  const cipher = ctr(encryptionKey, iv)
  const ciphertext = cipher.encrypt(payloadBytes)

  // Generate MAC: keccak256(derivedKey[16:32] || ciphertext)
  const macKey = derivedKey.slice(16, 32)
  const macInput = new Uint8Array([...macKey, ...ciphertext])
  const mac = keccak_256(macInput)

  // Build keystore structure
  const keystore: FairdropKeystore = {
    version: 1,
    type: 'fairdrop',
    address: `${account.subdomain}.fairdrop.eth`,
    crypto: {
      cipher: 'aes-128-ctr',
      ciphertext: bytesToHex(ciphertext),
      cipherparams: { iv: bytesToHex(iv) },
      kdf: 'scrypt',
      kdfparams: {
        dklen: FDS_SCRYPT_DKLEN,
        n: FDS_SCRYPT_N,
        r: FDS_SCRYPT_R,
        p: FDS_SCRYPT_P,
        salt: bytesToHex(salt),
      },
      mac: bytesToHex(mac),
    },
  }

  return JSON.stringify(keystore, null, 2)
}

/**
 * Decrypt a keystore and reconstruct the account
 * Supports FDS v1 (fairdrop/fairdrive) and Ethereum v3 (wallet import)
 */
export async function parseFairdropKeystore(
  keystoreJson: string,
  password: string,
  onProgress?: (stage: KeystoreProgress) => void
): Promise<Account> {
  // Parse keystore
  let keystore: AnyKeystore
  try {
    keystore = JSON.parse(keystoreJson)
  } catch {
    throw new Error('Invalid keystore: not valid JSON')
  }

  // Handle both 'crypto' and 'Crypto' for compatibility (some wallets use uppercase)
  const ksRecord = keystore as unknown as Record<string, unknown>
  if (!keystore.crypto && ksRecord.Crypto) {
    keystore.crypto = ksRecord.Crypto as FairdropKeystore['crypto']
  }

  // Detect format and validate
  if (isFDSKeystore(keystore)) {
    return parseFDSV1Keystore(keystore, password, onProgress)
  } else if (isEthereumKeystore(keystore)) {
    return parseEthereumV3Keystore(keystore, password, onProgress)
  } else if (validateKeystoreFormat(keystore)) {
    // Legacy path - try as FDS
    return parseFDSV1Keystore(keystore as FairdropKeystore, password, onProgress)
  } else {
    throw new Error('Invalid keystore format: unrecognized version or type')
  }
}

/**
 * Parse FDS v1 keystore (fairdrop/fairdrive format)
 * Uses @noble/ciphers for exact compatibility with fds-id-ts
 */
async function parseFDSV1Keystore(
  keystore: FairdropKeystore,
  password: string,
  onProgress?: (stage: KeystoreProgress) => void
): Promise<Account> {
  const { crypto } = keystore

  // Extract parameters using @noble/hashes hexToBytes (no 0x prefix expected)
  const salt = hexToBytes(crypto.kdfparams.salt)
  const iv = hexToBytes(crypto.cipherparams.iv)
  const ciphertext = hexToBytes(crypto.ciphertext)
  const storedMac = hexToBytes(crypto.mac)

  // Use params from keystore
  const scryptN = crypto.kdfparams.n
  const scryptR = crypto.kdfparams.r
  const scryptP = crypto.kdfparams.p
  const scryptDkLen = crypto.kdfparams.dklen

  // Derive key using scrypt
  onProgress?.('deriving-key')
  const derivedKey = await deriveKeyAsync(password, salt, scryptN, scryptR, scryptP, scryptDkLen)

  // Verify MAC: keccak256(derivedKey[16:32] || ciphertext)
  onProgress?.('verifying')
  const macInput = new Uint8Array([...derivedKey.slice(16, 32), ...ciphertext])
  const computedMac = keccak_256(macInput)

  if (!constantTimeEqual(computedMac, storedMac)) {
    throw new Error('Incorrect password')
  }

  // Decrypt payload using @noble/ciphers
  onProgress?.('decrypting')
  const encryptionKey = derivedKey.slice(0, 16)
  const cipher = ctr(encryptionKey, iv)
  const payloadBytes = cipher.decrypt(ciphertext)
  const payloadJson = new TextDecoder().decode(payloadBytes)

  let payload: FairdropKeystorePayload
  try {
    payload = JSON.parse(payloadJson)
  } catch {
    throw new Error('Decryption failed: corrupted payload')
  }

  // Validate required fields
  if (!payload.subdomain || !payload.publicKey || !payload.privateKey) {
    throw new Error('Invalid keystore: missing required fields in payload')
  }

  // Reconstruct account
  const account: Account = {
    subdomain: payload.subdomain,
    publicKey: payload.publicKey,
    privateKey: payload.privateKey,
    passwordHash: await hashPassword(password),
    inboxParams: payload.inboxParams,
    walletAddress: payload.walletAddress || undefined,
    created: payload.created || Date.now(),
    // Restore postage stamp info from backup
    stampId: payload.stampId,
    stampExpiresAt: payload.stampExpiresAt,
    stampCapacity: payload.stampCapacity,
    stampTotalCapacity: payload.stampTotalCapacity,
  }

  return account
}

/**
 * Parse Ethereum Web3 Secret Storage v3 keystore (wallet import)
 * This contains only a private key, not the full account data
 */
async function parseEthereumV3Keystore(
  keystore: EthereumKeystore,
  password: string,
  onProgress?: (stage: KeystoreProgress) => void
): Promise<Account> {
  const { crypto } = keystore

  // Extract parameters using @noble/hashes hexToBytes
  const salt = hexToBytes(crypto.kdfparams.salt)
  const iv = hexToBytes(crypto.cipherparams.iv)
  const ciphertext = hexToBytes(crypto.ciphertext)
  const storedMac = hexToBytes(crypto.mac)

  // Use params from keystore
  const scryptN = crypto.kdfparams.n
  const scryptR = crypto.kdfparams.r
  const scryptP = crypto.kdfparams.p
  const scryptDkLen = crypto.kdfparams.dklen

  // Derive key using scrypt
  onProgress?.('deriving-key')
  const derivedKey = await deriveKeyAsync(password, salt, scryptN, scryptR, scryptP, scryptDkLen)

  // Verify MAC: keccak256(derivedKey[16:32] || ciphertext)
  onProgress?.('verifying')
  const macInput = new Uint8Array([...derivedKey.slice(16, 32), ...ciphertext])
  const computedMac = keccak_256(macInput)

  if (!constantTimeEqual(computedMac, storedMac)) {
    throw new Error('Incorrect password')
  }

  // Decrypt private key using @noble/ciphers
  onProgress?.('decrypting')
  const encryptionKey = derivedKey.slice(0, 16)
  const cipher = ctr(encryptionKey, iv)
  const privateKeyBytes = cipher.decrypt(ciphertext)

  // Convert private key to hex (with 0x prefix for ethers)
  const privateKey = '0x' + bytesToHex(privateKeyBytes)

  // Derive public key from private key
  const { SigningKey } = await import('ethers')
  const signingKey = new SigningKey(privateKey)
  const publicKey = signingKey.compressedPublicKey

  // The address in the keystore is the Ethereum address (without 0x)
  const walletAddress = '0x' + keystore.address

  // For v3 keystores, use first 8 chars of address as temporary subdomain
  const subdomain = keystore.address.slice(0, 8).toLowerCase()

  // Reconstruct account with available data
  const account: Account = {
    subdomain,
    publicKey: publicKey.slice(2), // Remove 0x prefix
    privateKey: privateKey.slice(2), // Remove 0x prefix
    passwordHash: await hashPassword(password),
    walletAddress,
    created: Date.now(),
  }

  return account
}

/**
 * Validate keystore structure (supports FDS v1 and Ethereum v3)
 */
export function validateKeystoreFormat(json: unknown): json is AnyKeystore {
  if (!json || typeof json !== 'object') return false

  const ks = json as Record<string, unknown>

  // Check for Ethereum v3 format first
  if (ks.version === 3 && typeof ks.id === 'string') {
    return validateEthereumKeystoreFormat(ks)
  }

  // Check for FDS v1 format (fairdrop or fairdrive)
  if (ks.version === 1 && (ks.type === 'fairdrop' || ks.type === 'fairdrive')) {
    return validateFDSKeystoreFormat(ks)
  }

  return false
}

/**
 * Validate FDS v1 keystore format
 */
function validateFDSKeystoreFormat(ks: Record<string, unknown>): boolean {
  if (ks.version !== 1) return false
  if (ks.type !== 'fairdrop' && ks.type !== 'fairdrive') return false
  if (typeof ks.address !== 'string') return false

  // Handle both 'crypto' and 'Crypto'
  const crypto = (ks.crypto || ks.Crypto) as Record<string, unknown> | undefined
  if (!crypto || typeof crypto !== 'object') return false

  if (crypto.cipher !== 'aes-128-ctr') return false
  if (crypto.kdf !== 'scrypt') return false
  if (typeof crypto.ciphertext !== 'string') return false
  if (typeof crypto.mac !== 'string') return false

  if (!crypto.cipherparams || typeof crypto.cipherparams !== 'object') return false
  if (!crypto.kdfparams || typeof crypto.kdfparams !== 'object') return false

  const cipherparams = crypto.cipherparams as Record<string, unknown>
  const kdfparams = crypto.kdfparams as Record<string, unknown>

  if (typeof cipherparams.iv !== 'string') return false
  if (typeof kdfparams.salt !== 'string') return false
  if (typeof kdfparams.n !== 'number') return false
  if (typeof kdfparams.r !== 'number') return false
  if (typeof kdfparams.p !== 'number') return false
  if (typeof kdfparams.dklen !== 'number') return false

  return true
}

/**
 * Validate Ethereum v3 keystore format
 */
function validateEthereumKeystoreFormat(ks: Record<string, unknown>): boolean {
  if (ks.version !== 3) return false
  if (typeof ks.id !== 'string') return false
  if (typeof ks.address !== 'string') return false

  // Handle both 'crypto' and 'Crypto'
  const crypto = (ks.crypto || ks.Crypto) as Record<string, unknown> | undefined
  if (!crypto || typeof crypto !== 'object') return false

  if (crypto.cipher !== 'aes-128-ctr') return false
  if (crypto.kdf !== 'scrypt') return false
  if (typeof crypto.ciphertext !== 'string') return false
  if (typeof crypto.mac !== 'string') return false

  if (!crypto.cipherparams || typeof crypto.cipherparams !== 'object') return false
  if (!crypto.kdfparams || typeof crypto.kdfparams !== 'object') return false

  const cipherparams = crypto.cipherparams as Record<string, unknown>
  const kdfparams = crypto.kdfparams as Record<string, unknown>

  if (typeof cipherparams.iv !== 'string') return false
  if (typeof kdfparams.salt !== 'string') return false
  if (typeof kdfparams.n !== 'number') return false
  if (typeof kdfparams.r !== 'number') return false
  if (typeof kdfparams.p !== 'number') return false
  if (typeof kdfparams.dklen !== 'number') return false

  return true
}

/**
 * Get subdomain from keystore without decrypting
 */
export function getKeystoreSubdomain(keystoreJson: string): string | null {
  try {
    const keystore = JSON.parse(keystoreJson)

    // Check for .fairdrop.eth or .fairdrive.eth address (FDS format)
    if (keystore.address && typeof keystore.address === 'string') {
      const addr = keystore.address.toLowerCase()
      if (addr.endsWith('.fairdrop.eth') || addr.endsWith('.fairdrive.eth')) {
        // Extract first part before the dot
        const parts = keystore.address.split('.')
        return parts[0] || null
      }
    }

    // Ethereum v3 format: address is the Ethereum address
    if (keystore.version === 3 && keystore.address && typeof keystore.address === 'string') {
      // Return first 8 chars as preview (user will set real subdomain)
      return keystore.address.slice(0, 8).toLowerCase()
    }
  } catch {
    // Invalid JSON
  }
  return null
}

// ============================================================
// Private helper functions
// ============================================================

/**
 * Constant-time comparison to prevent timing attacks on MAC verification.
 */
function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  let result = 0
  for (let i = 0; i < a.length; i++) {
    result |= a[i] ^ b[i]
  }
  return result === 0
}

/**
 * Derive encryption key using scrypt (async for UI responsiveness)
 * Uses @noble/hashes which is synchronous, so we wrap in setTimeout
 */
async function deriveKeyAsync(
  password: string,
  salt: Uint8Array,
  n: number,
  r: number,
  p: number,
  dkLen: number
): Promise<Uint8Array> {
  const passwordBytes = new TextEncoder().encode(password)

  // Run in next tick to allow UI to update
  return new Promise((resolve) => {
    setTimeout(() => {
      const key = scrypt(passwordBytes, salt, {
        N: n,
        r: r,
        p: p,
        dkLen: dkLen,
      })
      resolve(key)
    }, 0)
  })
}
